import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Menu,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add,
  DescriptionOutlined,
  DeleteOutlineOutlined,
  EditOutlined,
  VisibilityOutlined,
  SearchOutlined,
  RefreshOutlined,
  CurrencyRupeeOutlined,
  PendingActionsOutlined,
  TaskAltOutlined,
  ExpandLess as ExpandLessIcon,
  MoreVert as MoreVertIcon,
  ShoppingCartOutlined,
  ReceiptLongOutlined,
  AssignmentOutlined,
  LocalShippingOutlined,
  PaymentOutlined,
  Download,
  PictureAsPdf,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import backgroundPattern from "../../assets/pasbookpng.png";
import "../Invoices/Invoice.css";
import "./EstimateQuotationList.css";
import { estimateApi } from "../../api";

const CREATE_PATH = "/dashboard/estimates/new";
const STATUS_FILTERS = ['All', 'Draft', 'Open', 'Sent', 'Accepted', 'Converted', 'Rejected', 'Expired'];

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const getStatus = (item) => {
  const value = String(item?.status || 'Draft').trim().toLowerCase();
  if (value === 'draft') return 'Draft';
  if (value === 'open' || value === 'sent') return 'Open';
  if (value === 'accepted' || value === 'confirmed') return 'Accepted';
  if (value === 'converted') return 'Converted';
  if (value === 'rejected' || value === 'expired') return 'Rejected';
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : 'Draft';
};

const getNumber = (item) =>
  item?.quotationNo ||
  item?.estimateNo ||
  item?.number ||
  `EST-${String(item?.id || "").slice(-6)}`;

const getCustomer = (item) =>
  item?.customer?.name ||
  item?.customerName ||
  item?.customer ||
  "-";

const getMobile = (item) =>
  item?.customer?.mobile ||
  item?.customerMobile ||
  item?.mobile ||
  "";

const Status = ({ item }) => {
  const status = getStatus(item);
  const className = status === 'Open' ? 'sent' : status === 'Rejected' ? 'overdue' : status === 'Converted' ? 'converted' : status.toLowerCase().replace(/\s+/g, '-');
  return (
    <Box className={`invoice-status invoice-status-${className}`}>
      <span />
      {status}
    </Box>
  );
};

export default function EstimateQuotationList() {
  const navigate = useNavigate();

  const [estimates, setEstimates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const [moreTarget, setMoreTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const response = await estimateApi.getAllEstimates({ size: 100 });
      const data = response.data?.content || response.data || [];
      setEstimates(data);
    } catch (error) {
      console.error('Failed to load estimates:', error);
      toast.error('Failed to load estimates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const stats = useMemo(() => {
    const totalValue = estimates.reduce((sum, item) => sum + Number(item.total ?? item.totalAmount ?? item.grandTotal ?? 0), 0);
    const accepted = estimates.filter((item) => getStatus(item) === 'Accepted')
      .reduce((sum, item) => sum + Number(item.total ?? item.totalAmount ?? item.grandTotal ?? 0), 0);
    const outstanding = estimates.filter((item) => {
      const status = getStatus(item);
      return status === 'Open' || status === 'Draft' || status === 'Sent' || status === 'Converted';
    });
    const outstandingValue = outstanding.reduce((sum, item) => sum + Number(item.total ?? item.totalAmount ?? item.grandTotal ?? 0), 0);
    const openCount = estimates.filter((item) => {
      const status = getStatus(item);
      return status === 'Open' || status === 'Draft' || status === 'Sent';
    }).length;
    const convertedCount = estimates.filter((item) => getStatus(item) === 'Converted').length;
    return {
      count: estimates.length,
      value: totalValue,
      accepted,
      outstanding: outstandingValue,
      openCount,
      convertedCount,
    };
  }, [estimates]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return [...estimates]
      .filter((item) => {
        const searchable = [
          getNumber(item),
          getCustomer(item),
          item?.refNo,
          item?.reference,
          item?.subject,
        ].filter(Boolean).join(' ').toLowerCase();
        const matchesSearch = !term || searchable.includes(term);
        const matchesStatus = filter === 'All' || getStatus(item) === filter;
        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => String(b.quotationDate || b.date || '').localeCompare(String(a.quotationDate || a.date || '')));
  }, [estimates, search, filter]);

  const removeEstimate = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await estimateApi.deleteEstimate(deleteTarget.id);
      const next = estimates.filter((item) => item.id !== deleteTarget.id);
      setEstimates(next);
      setDeleteTarget(null);
      setSelected(null);
      toast.success('Estimate deleted successfully.');
    } catch (error) {
      console.error('Estimate delete failed:', error);
      toast.error('Unable to delete estimate.');
    } finally {
      setDeleting(false);
    }
  };

  const downloadPdf = async (item) => {
    if (!item?.id) return;
    try {
      const response = await estimateApi.downloadEstimatePdf(item.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Quotation_${item.quotationNo || item.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Quotation PDF downloaded successfully.');
    } catch (error) {
      console.error('PDF download failed:', error);
      toast.error('Failed to download PDF. Please try again.');
    }
  };

  const previewPdf = async (item) => {
    if (!item?.id) return;
    try {
      const response = await estimateApi.previewEstimatePdf(item.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success('Quotation PDF preview opened.');
    } catch (error) {
      console.error('PDF preview failed:', error);
      toast.error('Failed to preview PDF. Please try again.');
    }
  };

  const editEstimate = (item) => {
    navigate(CREATE_PATH, { state: { editData: item } });
  };

  const clearFilters = () => {
    setSearch('');
    setFilter('All');
  };

  const handleMoreOpen = (event, item) => {
    event.stopPropagation();
    setMoreAnchorEl(event.currentTarget);
    setMoreTarget(item);
  };

  const handleMoreClose = () => {
    setMoreAnchorEl(null);
    setMoreTarget(null);
  };

  const canConvert = (item) => {
    const status = getStatus(item);
    return status === 'Accepted' || status === 'Open';
  };

  const canAccept = (item) => {
    const status = getStatus(item);
    return status === 'Open' || status === 'Draft' || status === 'Sent';
  };

  const acceptEstimate = async (item) => {
    handleMoreClose();
    try {
      await estimateApi.patchEstimate(item.id, { status: "Accepted" });
      const updated = estimates.map((est) =>
        est.id === item.id ? { ...est, status: "Accepted" } : est
      );
      setEstimates(updated);
      toast.success('Quotation accepted successfully.');
    } catch (error) {
      console.error('Failed to accept quotation:', error);
      toast.error('Unable to accept quotation.');
    }
  };

  const convertToDocument = async (targetPath, item) => {
    handleMoreClose();

    try {
      await estimateApi.patchEstimate(item.id, { status: "Converted" });
      const updated = estimates.map((est) =>
        est.id === item.id ? { ...est, status: "Converted" } : est
      );
      setEstimates(updated);
    } catch (error) {
      console.error("Failed to update quotation status:", error);
    }

    navigate(targetPath, { state: { quotationData: item } });
  };

  return (
    <Box className="invoice-page invoice-list-page">
      <div className="invoice-background-watermark" style={{ backgroundImage: `url(${backgroundPattern})` }} />

      <Box className="invoice-shell">
        <Box className="invoice-page-header">
          <Box className="invoice-page-header-left invoice-header-title-group">
            <Box className="invoice-page-icon"><DescriptionOutlined /></Box>
            <Box className="invoice-page-title-wrap">
              <Typography className="invoice-page-title">Estimates / Quotations</Typography>
              <Typography className="invoice-page-subtitle">Create, manage and track customer estimates and quotations.</Typography>
            </Box>
          </Box>
          <Button className="invoice-header-create-btn" startIcon={<Add />} onClick={() => navigate(CREATE_PATH)}>
            New Estimate
          </Button>
        </Box>

        <Box className="invoice-list-summary">
          <Card className="invoice-summary-card invoice-summary-card-total">
            <Box className="invoice-summary-icon"><DescriptionOutlined /></Box>
            <Box className="invoice-summary-body">
              <span className="invoice-summary-label">Total estimates</span>
              <strong>{stats.count}</strong>
              <small>All quotations</small>
            </Box>
          </Card>
          <Card className="invoice-summary-card invoice-summary-card-value">
            <Box className="invoice-summary-icon"><CurrencyRupeeOutlined /></Box>
            <Box className="invoice-summary-body">
              <span className="invoice-summary-label">Total value</span>
              <strong>{money(stats.value)}</strong>
              <small>Quotation value</small>
            </Box>
          </Card>
          <Card className="invoice-summary-card invoice-summary-card-paid">
            <Box className="invoice-summary-icon"><TaskAltOutlined /></Box>
            <Box className="invoice-summary-body">
              <span className="invoice-summary-label">Accepted</span>
              <strong>{money(stats.accepted)}</strong>
              <small>Confirmed quotations</small>
            </Box>
          </Card>
          <Card className="invoice-summary-card invoice-summary-card-outstanding">
            <Box className="invoice-summary-icon"><PendingActionsOutlined /></Box>
            <Box className="invoice-summary-body">
              <span className="invoice-summary-label">Outstanding</span>
              <strong>{money(stats.outstanding)}</strong>
              <small>{stats.openCount} open, {stats.convertedCount} converted</small>
            </Box>
          </Card>
        </Box>

        {!filtersOpen ? (
          <button
            type="button"
            className="invoice-show-filters-btn"
            onClick={() => setFiltersOpen(true)}
          >
            <SearchOutlined className="invoice-show-filters-icon" />
            <span>Show Filters</span>
          </button>
        ) : (
          <Card className="invoice-filter-card">
            <CardContent>
              <Box className="invoice-filter-head">
                <Box className="invoice-filter-head-left">
                  <button
                    type="button"
                    className="invoice-filter-title-btn"
                    onClick={() => setFiltersOpen(false)}
                    aria-label="Hide filters"
                    title="Hide filters"
                  >
                    <Box className="invoice-filter-icon"><SearchOutlined /></Box>
                    <Typography className="invoice-filter-title">Filters</Typography>
                    <ExpandLessIcon className="invoice-filter-collapse-icon" />
                  </button>
                </Box>
                <Button
                  className="invoice-filter-clear"
                  onClick={clearFilters}
                  disabled={!search && filter === 'All'}
                >
                  Clear
                </Button>
              </Box>
              <Stack className="invoice-filter-controls" direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <TextField
                  className="invoice-field invoice-search-field"
                  placeholder="Search quotation number, customer or reference"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{ startAdornment: <SearchOutlined sx={{ color: '#94a3b8', mr: 1 }} /> }}
                />
                <TextField select className="invoice-field invoice-status-filter" value={filter} onChange={(e) => setFilter(e.target.value)}>
                  {STATUS_FILTERS.map((item) => (
                    <MenuItem key={item} value={item}>{item === 'All' ? 'All statuses' : item}</MenuItem>
                  ))}
                </TextField>
                <Typography className="invoice-results-count">
                  {filtered.length} quotation{filtered.length === 1 ? '' : 's'}
                </Typography>
                <IconButton
                  className="invoice-row-action"
                  onClick={(event) => {
                    load();

                    const icon = event.currentTarget.querySelector('.invoice-refresh-icon');
                    if (!icon || typeof icon.animate !== 'function') return;

                    icon.getAnimations().forEach((animation) => animation.cancel());

                    const animation = icon.animate(
                      [
                        { transform: 'rotate(0deg)' },
                        { transform: 'rotate(360deg)' },
                      ],
                      {
                        duration: 1000,
                        iterations: 1,
                        easing: 'linear',
                        fill: 'none',
                      }
                    );

                    animation.onfinish = () => {
                      icon.style.transform = '';
                    };
                  }}
                  title="Refresh"
                  aria-label="Refresh estimates"
                >
                  <RefreshOutlined className="invoice-refresh-icon" />
                </IconButton>
              </Stack>
            </CardContent>
          </Card>
        )}

        <Card className="invoice-list-card">
          {loading ? (
            <Box className="invoice-loading"><CircularProgress size={28} /><Typography>Loading estimates…</Typography></Box>
          ) : filtered.length === 0 ? (
            <Box className="invoice-empty">
              <Box className="invoice-empty-icon"><DescriptionOutlined /></Box>
              <Typography>No estimates found</Typography>
              <span>{estimates.length ? 'Try changing your search or status filter.' : 'Create your first estimate / quotation to see it here.'}</span>
              {!estimates.length && (
                <Button className="invoice-primary-btn" startIcon={<Add />} onClick={() => navigate(CREATE_PATH)}>
                  Create Estimate
                </Button>
              )}
            </Box>
          ) : (
            <Box className="invoice-table-container">
              <Table className="invoice-professional-table invoice-responsive-list-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Quotation #</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Quotation date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((item) => (
                    <TableRow key={item.id || getNumber(item)} hover>
                      <TableCell data-label="Quotation #">
                        <button type="button" className="invoice-number-link" onClick={() => setSelected(item)}>
                          {getNumber(item)}
                        </button>
                      </TableCell>
                      <TableCell data-label="Customer">
                        <Box>
                          <Typography className="invoice-customer-name">{getCustomer(item)}</Typography>
                          {getMobile(item) && <Typography className="invoice-customer-phone">{getMobile(item)}</Typography>}
                        </Box>
                      </TableCell>
                      <TableCell data-label="Reference">{item.refNo || item.reference || '-'}</TableCell>
                      <TableCell data-label="Quotation date">{formatDate(item.quotationDate || item.date)}</TableCell>
                      <TableCell data-label="Status"><Status item={item} /></TableCell>
                      <TableCell data-label="Total" align="right"><Typography className="invoice-amount">{money(item.total ?? item.totalAmount ?? item.grandTotal)}</Typography></TableCell>
                      <TableCell data-label="Actions">
                        <Box className="invoice-row-actions">
                          <IconButton className="invoice-row-action invoice-row-view" title="View" onClick={() => setSelected(item)}>
                            <VisibilityOutlined />
                          </IconButton>
                          <IconButton className="invoice-row-action invoice-row-preview" title="Preview PDF" onClick={(e) => { e.stopPropagation(); previewPdf(item); }}>
                            <PictureAsPdf />
                          </IconButton>
                          <IconButton className="invoice-row-action invoice-row-download" title="Download PDF" onClick={(e) => { e.stopPropagation(); downloadPdf(item); }}>
                            <Download />
                          </IconButton>
                          <IconButton className="invoice-row-action invoice-row-edit" title="Edit" onClick={() => editEstimate(item)}>
                            <EditOutlined />
                          </IconButton>
                          {canConvert(item) && (
                            <IconButton className="invoice-row-action" title="More actions" onClick={(e) => handleMoreOpen(e, item)}>
                              <MoreVertIcon />
                            </IconButton>
                          )}
                          {!canConvert(item) && (
                            <IconButton className="invoice-row-action invoice-row-delete" title="Delete" onClick={() => setDeleteTarget(item)}>
                              <DeleteOutlineOutlined />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Card>
      </Box>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="md">
        <DialogTitle>{selected ? `Estimate / Quotation ${getNumber(selected)}` : 'Estimate / Quotation'}</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                <Box flex={1}>
                  <Typography variant="caption">Customer</Typography>
                  <Typography fontWeight={700}>{getCustomer(selected)}</Typography>
                  {selected.customerSource && (
                    <Typography variant="caption" sx={{
                      display: 'inline-block',
                      mt: 0.5,
                      px: 1,
                      py: 0.25,
                      borderRadius: '999px',
                      fontSize: '10px',
                      fontWeight: 700,
                      bgcolor: selected.customerSource === 'existing' ? '#dcfce7' : '#fef3c7',
                      color: selected.customerSource === 'existing' ? '#166534' : '#92400e',
                    }}>
                      {selected.customerSource === 'existing' ? 'Existing Customer' : 'Quotation Customer'}
                    </Typography>
                  )}
                </Box>
                <Box flex={1}><Typography variant="caption">Date</Typography><Typography fontWeight={700}>{formatDate(selected.quotationDate || selected.date)}</Typography></Box>
                <Box flex={1}><Typography variant="caption">Reference</Typography><Typography fontWeight={700}>{selected.refNo || selected.reference || '-'}</Typography></Box>
                <Box flex={1}><Typography variant="caption">Total</Typography><Typography fontWeight={800}>{money(selected.total ?? selected.totalAmount ?? selected.grandTotal)}</Typography></Box>
              </Stack>
              <Typography variant="subtitle2">Cylinder Details</Typography>
              <Table size="small">
                <TableHead><TableRow><TableCell>#</TableCell><TableCell>Cylinder</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Rate</TableCell><TableCell align="right">GST %</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
                <TableBody>
                  {(selected.cylinders || []).map((row, index) => {
                    const qty = Number(row.qty || 0);
                    const rate = Number(row.rate || 0);
                    const gst = Number(row.gst || 0);
                    const gstAmount = (qty * rate * gst) / 100;
                    return (
                      <TableRow key={row.id || index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{row.cylinderType || row.cylinderName || '-'}</TableCell>
                        <TableCell align="right">{qty}</TableCell>
                        <TableCell align="right">{money(rate)}</TableCell>
                        <TableCell align="right">{gst}%</TableCell>
                        <TableCell align="right">{money(qty * rate + gstAmount)}</TableCell>
                      </TableRow>
                    );
                  })}
                  {!(selected.cylinders || []).length && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">No cylinder details available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
          {selected && (
            <Button variant="outlined" startIcon={<PictureAsPdf />} onClick={() => previewPdf(selected)}>
              Preview PDF
            </Button>
          )}
          {selected && (
            <Button variant="outlined" startIcon={<Download />} onClick={() => downloadPdf(selected)}>
              Download PDF
            </Button>
          )}
          {selected && <Button variant="contained" onClick={() => { setSelected(null); editEstimate(selected); }}>Edit Estimate</Button>}
          {selected && canAccept(selected) && (
            <Button
              variant="contained"
              color="success"
              startIcon={<TaskAltOutlined />}
              onClick={() => {
                acceptEstimate(selected);
                setSelected(null);
              }}
            >
              Accept
            </Button>
          )}
          {selected && canConvert(selected) && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<ShoppingCartOutlined />}
                onClick={() => {
                  const quotationData = selected;
                  setSelected(null);
                  convertToDocument('/dashboard/sales-orders/new', quotationData);
                }}
              >
                Create Sales Order
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<ReceiptLongOutlined />}
                onClick={() => {
                  const quotationData = selected;
                  setSelected(null);
                  convertToDocument('/dashboard/proforma-invoices/new', quotationData);
                }}
              >
                Create PI
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<AssignmentOutlined />}
                onClick={() => {
                  const quotationData = selected;
                  setSelected(null);
                  convertToDocument('/dashboard/customer-pos/new', quotationData);
                }}
              >
                Record Customer PO
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<LocalShippingOutlined />}
                onClick={() => {
                  const quotationData = selected;
                  setSelected(null);
                  convertToDocument('/dashboard/challans/delivered', quotationData);
                }}
              >
                Create DC
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<PaymentOutlined />}
                onClick={() => {
                  const quotationData = selected;
                  setSelected(null);
                  convertToDocument('/dashboard/invoices/create', quotationData);
                }}
              >
                Create Invoice
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Estimate?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">This will permanently remove {deleteTarget ? getNumber(deleteTarget) : 'this estimate'} from the saved estimates.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={removeEstimate} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={moreAnchorEl}
        open={Boolean(moreAnchorEl)}
        onClose={handleMoreClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {moreTarget && canAccept(moreTarget) && (
          <MenuItem onClick={() => acceptEstimate(moreTarget)}>
            <ListItemIcon><TaskAltOutlined fontSize="small" sx={{ color: '#16a34a' }} /></ListItemIcon>
            <ListItemText sx={{ color: '#16a34a' }}>Accept Quotation</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => convertToDocument('/dashboard/sales-orders/new', moreTarget)}>
          <ListItemIcon><ShoppingCartOutlined fontSize="small" /></ListItemIcon>
          <ListItemText>Create Sales Order</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => convertToDocument('/dashboard/proforma-invoices/new', moreTarget)}>
          <ListItemIcon><ReceiptLongOutlined fontSize="small" /></ListItemIcon>
          <ListItemText>Create PI</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => convertToDocument('/dashboard/customer-pos/new', moreTarget)}>
          <ListItemIcon><AssignmentOutlined fontSize="small" /></ListItemIcon>
          <ListItemText>Record Customer PO</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => convertToDocument('/dashboard/challans/delivered', moreTarget)}>
          <ListItemIcon><LocalShippingOutlined fontSize="small" /></ListItemIcon>
          <ListItemText>Create Delivery Challan</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => convertToDocument('/dashboard/invoices/create', moreTarget)}>
          <ListItemIcon><PaymentOutlined fontSize="small" /></ListItemIcon>
          <ListItemText>Create Invoice</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleMoreClose(); downloadPdf(moreTarget); }}>
          <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleMoreClose(); previewPdf(moreTarget); }}>
          <ListItemIcon><VisibilityOutlined fontSize="small" /></ListItemIcon>
          <ListItemText>Preview PDF</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleMoreClose(); setDeleteTarget(moreTarget); }} sx={{ color: '#DC2626' }}>
          <ListItemIcon><DeleteOutlineOutlined fontSize="small" sx={{ color: '#DC2626' }} /></ListItemIcon>
          <ListItemText>Delete Quotation</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}